import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { TrashIcon } from "@heroicons/react/outline";
import { groupBy, sortBy } from "lodash";
import { Fragment, useCallback, useMemo, useState } from "react";
import {
  CompetencyAppliesTo,
  CompetencyCriteriaUniqueness,
  DeleteOrgWideCompetencyMutation,
  DeleteOrgWideCompetencyMutationVariables,
  GetOrganizationCompetenciesQuery,
  GetOrganizationCompetenciesQueryVariables,
} from "types/graphql-schema";

import deleteOrgWideCompetencyMutation from "@apps/org-settings/graphql/delete-org-wide-competency-mutation";
import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import Placeholder from "@components/placeholder/placeholder";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import getOrganizationCompetencies from "../../graphql/get-organization-competencies-query";
import useCareerPermissions, {
  LEAD_DELETE_ERROR_MATCHES,
} from "./career-permissions";
import CompetencyFormModal from "./competency-form-modal";
import { getCompetencySharing } from "./competency-sharing";
import { sectionHeaderToneClassName } from "./competency-sharing-tone";
import {
  CompetencyScopeSection,
  allCompetenciesPath,
  getCompetencyScopeSection,
} from "./helpers";
import ScopeSectionIcon from "./scope-section-icon";
import StickyColumnShadow from "./sticky-column-shadow";

const CompetenciesList = ({ organizationId }: { organizationId: number }) => {
  const label = useLabel();
  const link = useLink();
  const { canCreate, canEditCompetency } = useCareerPermissions();

  const {
    confirm: confirmDeleteCompetency,
    ConfirmationDialog: ConfirmationDialogDeleteCompetency,
  } = useConfirm(
    `Are you sure?`,
    `Are you sure you want to remove this ${label("competency")}?`,
  );

  const [editingCompetencyId, setEditingCompetencyId] = useState<number | null>(
    null,
  );
  const [isCreatingCompetency, setIsCreatingCompetency] = useState(false);

  const { data, loading } = useQuery<
    GetOrganizationCompetenciesQuery,
    GetOrganizationCompetenciesQueryVariables
  >(getOrganizationCompetencies, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    variables: {
      organizationId,
    },
  });

  // The overview's track table counts these too, from its own copy of the
  // same query: refetch every copy, not just this one.
  const client = useApolloClient();
  const refetchAllCompetencies = () =>
    client.refetchQueries({ include: [getOrganizationCompetencies] });

  const [deleteCompetency, { loading: isDeleting }] = useMutation<
    DeleteOrgWideCompetencyMutation,
    DeleteOrgWideCompetencyMutationVariables
  >(deleteOrgWideCompetencyMutation, {
    context: { errorMatches: LEAD_DELETE_ERROR_MATCHES },
    onCompleted: () => {
      successNotificationVar({
        title: `${label("competency", { capitalize: true })} Deleted`,
      });
    },
    refetchQueries: [getOrganizationCompetencies],
  });

  const handleDeleteCompetency = useCallback(
    async (competencyId: number) => {
      const confirmed = await confirmDeleteCompetency();
      if (confirmed) {
        deleteCompetency({
          variables: {
            competencyId,
          },
        });
      }
    },
    [confirmDeleteCompetency, deleteCompetency],
  );

  const competenciesLabel = label("competency", {
    pluralize: true,
    capitalize: true,
  });

  const competencies = useMemo(
    () => (data?.competencies ? assertEdgesNonNull(data.competencies) : []),
    [data],
  );

  // The same three sections a career track's table has, by how far each
  // competency reaches.
  const sections = useMemo(() => {
    const byScope = groupBy(
      sortBy(competencies, (competency) => competency.title.toLowerCase()),
      (competency) => getCompetencyScopeSection(competency),
    );
    const definitions: {
      key: CompetencyScopeSection;
      title: string;
    }[] = [
      // Narrowest first, company-wide last: the career track editor's order.
      {
        key: "track",
        title: "Single Track",
      },
      {
        key: "shared",
        title: "Shared Across Tracks",
      },
      {
        key: "company",
        title: "Company-Wide",
      },
    ];
    return (
      definitions
        .map((definition) => ({
          ...definition,
          competencies: byScope[definition.key] ?? [],
        }))
        // Company-wide and single-track always show, so the list says what
        // kinds there are. Shared is a shape a competency grows into rather
        // than one you set out to make, so an empty header would name
        // something there is no way to add.
        .filter(
          (section) =>
            section.key !== "shared" || section.competencies.length > 0,
        )
    );
  }, [competencies]);

  // Every cell but the pinned name draws the line on its left and below.
  // Small gray labels on a light gray band, like the People table's header.
  const headerCellClassName =
    "py-3 text-xs font-medium text-gray-500 border-b border-l sticky top-0 z-20 bg-gray-100";
  const bodyCellClassName = "px-4 py-3 border-b border-l";

  return (
    <div>
      <ConfirmationDialogDeleteCompetency />
      {editingCompetencyId !== null && (
        <CompetencyFormModal
          organizationId={organizationId}
          competencyId={editingCompetencyId}
          onClose={() => setEditingCompetencyId(null)}
          onCompetencySaved={() => {
            setEditingCompetencyId(null);
            refetchAllCompetencies();
          }}
          onCompetencyDeleted={() => {
            setEditingCompetencyId(null);
            refetchAllCompetencies();
          }}
        />
      )}
      {isCreatingCompetency && (
        <CompetencyFormModal
          organizationId={organizationId}
          onClose={() => setIsCreatingCompetency(false)}
          onCompetencySaved={() => {
            setIsCreatingCompetency(false);
            refetchAllCompetencies();
          }}
        />
      )}

      <div className="flex justify-between items-center mb-3">
        <div className="text-lg font-bold">{competenciesLabel}</div>
        <div className="flex items-center gap-2">
          {/* Every competency's criteria, level by level, in one table. */}
          <Button
            size={ButtonSize.medium}
            aria-label={`Edit all ${label("competency", { pluralize: true })}`}
            onClick={() => link.redirect(allCompetenciesPath(organizationId))}
          >
            <TopicflowIcon type="edit" className="h-4 w-4" />
            {`Edit All ${competenciesLabel}`}
          </Button>
          {/* Opens in the modal, like clicking a row below. */}
          {/* The same bordered button as the track table's Create/Add. */}
          {canCreate && (
            <Button
              size={ButtonSize.medium}
              aria-label={`Create ${label("competency")}`}
              onClick={() => setIsCreatingCompetency(true)}
            >
              <TopicflowIcon type="plus" className="h-4 w-4" />
              Create
            </Button>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center mt-8">
          <Loading />
        </div>
      )}
      {!loading && competencies.length === 0 && (
        <Placeholder>No {competenciesLabel} yet.</Placeholder>
      )}

      {competencies.length > 0 && (
        // A table: which kind of reach a competency has is a column to scan
        // down, with exactly who and how criteria are written beside it.
        // Scrolls in its own box, both ways, so the header row can pin to its
        // top and the names to its left: sticky cells stick to the nearest
        // scrolling box, and once the table scrolls sideways that's this one.
        <div className="mb-6 rounded-md border bg-white overflow-auto max-h-[calc(100vh-11rem)]">
          {/* Separate borders, each cell drawing its own lines: collapsed
              borders stay put while sticky cells move, and would leave the
              pinned header and names without theirs. */}
          <table
            className="w-full min-w-[40rem] text-sm table-fixed border-separate border-spacing-0 [&>tbody>tr:last-child>td]:border-b-0"
            aria-label={`${competenciesLabel} table`}
          >
            <thead>
              <tr className="text-left">
                {/* Wide enough for a name on one line; Applies To takes the
                    rest, and wraps first. Pinned both ways: the corner. */}
                <th className="w-96 px-4 py-3 text-xs font-medium text-gray-500 border-b border-r sticky top-0 left-0 z-30 bg-gray-100">
                  {label("competency", { capitalize: true })}
                  <StickyColumnShadow />
                </th>
                <th
                  className={classNames(headerCellClassName, "px-4 border-l-0")}
                >
                  Applies To
                </th>
                <th className={classNames(headerCellClassName, "w-44 px-4")}>
                  Criteria
                </th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => (
                <Fragment key={section.key}>
                  {/* A cell per column, so the column lines run through the
                      header too. The name cell stays in view while the row
                      scrolls sideways. */}
                  <tr className={sectionHeaderToneClassName}>
                    <td
                      className={classNames(
                        sectionHeaderToneClassName,
                        "px-4 py-2 font-bold border-b border-r sticky left-0 z-10",
                      )}
                    >
                      {section.title}
                      <StickyColumnShadow />
                    </td>
                    <td
                      className={classNames(
                        bodyCellClassName,
                        "py-2 border-l-0",
                      )}
                    />
                    <td className={classNames(bodyCellClassName, "py-2")} />
                  </tr>
                  {section.competencies.map((competency) => {
                    const sharing = getCompetencySharing(competency, {
                      label,
                      context: "list",
                    });
                    // A department lead opens only what reaches no further
                    // than their departments.
                    const canEdit = canEditCompetency(competency);
                    return (
                      <tr
                        key={competency.id}
                        className={classNames(
                          "group",
                          canEdit && "cursor-pointer hover:bg-gray-50",
                        )}
                        role={canEdit ? "button" : undefined}
                        tabIndex={canEdit ? 0 : undefined}
                        aria-label={
                          canEdit
                            ? `Edit ${competency.title}`
                            : competency.title
                        }
                        onClick={
                          canEdit
                            ? () => setEditingCompetencyId(competency.id)
                            : undefined
                        }
                        onKeyDown={(e) => {
                          // Only the row itself: Enter on the bin button deletes.
                          if (
                            canEdit &&
                            e.key === "Enter" &&
                            e.target === e.currentTarget
                          ) {
                            setEditingCompetencyId(competency.id);
                          }
                        }}
                      >
                        <td
                          className={classNames(
                            "px-4 py-3 font-medium border-b border-r sticky left-0 z-10 bg-white",
                            canEdit && "group-hover:bg-gray-50",
                          )}
                        >
                          {/* Delete sits at the name's far end, shown on
                              hover. */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2">
                              <ScopeSectionIcon
                                scope={section.key}
                                className="mt-0.5"
                              />
                              {competency.title}
                            </div>
                            {canEdit && (
                              <div className="-my-1 flex shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                                <Button
                                  theme={buttonTheme.iconGray}
                                  icon={TrashIcon}
                                  size={ButtonSize.medium}
                                  tooltip={`Delete ${label("competency")}`}
                                  aria-label={`Delete ${label("competency")}`}
                                  onClick={(evt) => {
                                    evt.stopPropagation();
                                    handleDeleteCompetency(competency.id);
                                  }}
                                  disabled={isDeleting}
                                />
                              </div>
                            )}
                          </div>
                          <StickyColumnShadow />
                        </td>
                        <td
                          className={classNames(
                            bodyCellClassName,
                            "text-gray-700 border-l-0",
                          )}
                          title={sharing.tooltip}
                        >
                          {competency.appliesTo === CompetencyAppliesTo.AllRoles
                            ? "All tracks"
                            : sharing.reach}
                        </td>
                        {/* Worded as the dialog's own choice is. */}
                        <td
                          className={classNames(
                            bodyCellClassName,
                            "text-gray-700",
                          )}
                        >
                          {competency.criteriaUniqueness ===
                          CompetencyCriteriaUniqueness.SameAcrossRoles
                            ? "Same across roles"
                            : "Unique across roles"}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CompetenciesList;
